import { useEffect, useState } from 'react'

import Loader from '../../../common/components/Loader'
import { IColumn, Table } from '../../../core/components/Table'
import { sanitizeDate } from '../../../core/constants/times'
import { getCheckInHistory } from '../../../core/services/searchBy'
import { MessageBox } from '../messages/MessageBox'
import { Modal } from '../../../components/ui/modal'
import { PageTitle } from '@/components'

export interface CheckinHistoryModalProps {
  ticketCode: string
  showCheckinHistoryModal: boolean
  setShowCheckinHistoryModal: (s: boolean) => void
  onClose?: () => void
}

interface IHistory {
  phone: string
  ticketCode: string
  userCard: string
  paymentProviderId: string
  paidAt: number | null
  fullPrice: number
  status:
    | 'NOT_AVAILABLE'
    | 'NOT_STARTED'
    | 'IN_PROGRESS'
    | 'PAYMENT_PENDING'
    | 'DONE'
    | 'REFUNDED'
  deletedAt: number | null
}

export const CheckinHistoryModal = (
  props: CheckinHistoryModalProps,
): JSX.Element => {
  const {
    ticketCode,
    showCheckinHistoryModal,
    setShowCheckinHistoryModal,
    onClose,
  } = props

  const [loading, setLoading] = useState<boolean>(true)
  const [history, setHistory] = useState<IHistory[]>([])
  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const handleOpenChange = (open: boolean) => {
    setShowCheckinHistoryModal(open)
    if (!open) {
      onClose?.()
    }
  }

  useEffect(() => {
    // prettier-ignore
    (async () => {
      setLoading(true)
      try {
        if (!!ticketCode) {
          const resp = await getCheckInHistory(ticketCode)
          setHistory(resp.data)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [ticketCode])

  const cols: IColumn[] = [
    {
      title: 'User Phone',
      accessor: 'phone',
      sortable: true,
    },
    {
      title: 'Ticket Code',
      accessor: 'ticketCode',
      sortable: true,
    },
    {
      title: 'User Card',
      accessor: 'userCard',
      sortable: true,
    },
    {
      title: 'Provider Payment ID',
      accessor: 'paymentProviderId',
      sortable: true,
    },
    {
      title: 'Payment At',
      accessor: 'paidAt',
      sortable: true,
      render: (row) => <>{row.paidAt && sanitizeDate(row.paidAt)}</>,
    },
    {
      title: 'Full Price (RON)',
      accessor: 'fullPrice',
      sortable: true,
    },
    {
      title: 'Check-in Status',
      accessor: 'status',
      sortable: true,
    },
    {
      title: 'Check-in Deleted At',
      accessor: 'deletedAt',
      sortable: true,
      render: (row) => <>{row.deletedAt && sanitizeDate(row.deletedAt)}</>,
    },
  ]

  return (
    <>
      <Modal
        open={showCheckinHistoryModal}
        onOpenChange={handleOpenChange}
        title={<PageTitle title="Check-in History" />}
        size="lg"
      >
        {loading && <Loader />}
        <Table columns={cols} data={history} />
      </Modal>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
